import PipelineStage from './PipelineStage';

function ProjectOverview({ pipelineStatus, currentStage, onStageChange, experiments }) {
    const stages = [
        {
            id: 'data',
            number: '1️⃣',
            name: 'Data Discovery',
            agent: '🔍 Scout',
            description: 'Find and load datasets'
        },
        {
            id: 'engineer',
            number: '2️⃣',
            name: 'Data Engineering',
            agent: '🧹 Engineer',
            description: 'Clean and preprocess'
        },
        {
            id: 'train',
            number: '3️⃣',
            name: 'Model Training',
            agent: '🏗️ ML Engineer',
            description: 'Train and tune models'
        },
        {
            id: 'eval',
            number: '4️⃣',
            name: 'Evaluation',
            agent: '📊 Evaluator',
            description: 'Analyze results'
        },
        {
            id: 'deploy',
            number: '5️⃣',
            name: 'Deployment',
            agent: '🚀 MLOps',
            description: 'Deploy to production'
        }
    ];

    const bestExperiment = experiments.find(exp => exp.is_best);
    const runningExperiment = experiments.find(exp => exp.status === 'running');

    return (
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Project Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                🎯 Project Overview
                            </h2>
                            <p className="text-gray-600">
                                Track your ML pipeline progress and agent activities
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-600 mb-1">Current Stage</div>
                            <div className="text-xl font-bold text-blue-600 capitalize">
                                {currentStage}
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <div className="text-blue-600 text-sm font-medium mb-1">Experiments</div>
                            <div className="text-2xl font-bold text-blue-900">{experiments.length}</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                            <div className="text-green-600 text-sm font-medium mb-1">Best Accuracy</div>
                            <div className="text-2xl font-bold text-green-900">
                                {bestExperiment ? `${(bestExperiment.metrics.accuracy * 100).toFixed(1)}%` : '-'}
                            </div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                            <div className="text-orange-600 text-sm font-medium mb-1">Running</div>
                            <div className="text-2xl font-bold text-orange-900">
                                {runningExperiment ? '1' : '0'}
                            </div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <div className="text-purple-600 text-sm font-medium mb-1">Completed</div>
                            <div className="text-2xl font-bold text-purple-900">
                                {experiments.filter(e => e.status === 'complete').length}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pipeline Stages */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">ML Pipeline Stages</h3>
                    <div className="grid grid-cols-5 gap-4">
                        {stages.map((stage, index) => (
                            <div key={stage.id}>
                                <PipelineStage
                                    stage={stage}
                                    status={pipelineStatus[stage.id]}
                                    isActive={currentStage === stage.id}
                                    onClick={() => onStageChange(stage.id)}
                                />
                                {index < stages.length - 1 && (
                                    <div className="flex justify-center my-2">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">👥 Agent Activity Feed</h3>
                    <div className="space-y-3">
                        <ActivityItem
                            agent="🏗️ ML Engineer"
                            message="Training epoch 2/3... Val loss: 0.31"
                            time="Just now"
                            status="active"
                        />
                        <ActivityItem
                            agent="📊 Evaluator"
                            message="Monitoring metrics for possible overfitting"
                            time="1 min ago"
                            status="active"
                        />
                        <ActivityItem
                            agent="🧹 Data Engineer"
                            message="Completed tokenization of 25k samples"
                            time="5 min ago"
                            status="complete"
                        />
                        <ActivityItem
                            agent="🔍 Data Scout"
                            message="Loaded IMDB dataset successfully"
                            time="10 min ago"
                            status="complete"
                        />
                    </div>
                </div>

                {/* Recent Experiments */}
                {experiments.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">🔬 Recent Experiments</h3>
                        <div className="space-y-2">
                            {experiments.slice(0, 3).map(exp => (
                                <div
                                    key={exp.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${
                                            exp.status === 'running' ? 'bg-orange-500 animate-pulse' :
                                            exp.status === 'complete' ? 'bg-green-500' :
                                            'bg-gray-300'
                                        }`} />
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {exp.name}
                                                {exp.is_best && (
                                                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                                        ⭐ Best
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                {exp.model} • {Object.entries(exp.params).map(([k, v]) => `${k}:${v}`).join(', ')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">
                                            Acc: {(exp.metrics.accuracy * 100).toFixed(1)}%
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {exp.duration}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ActivityItem({ agent, message, time, status }) {
    return (
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <div className={`w-2 h-2 rounded-full mt-1.5 ${
                status === 'active' ? 'bg-green-500 animate-pulse' :
                status === 'complete' ? 'bg-blue-500' :
                'bg-gray-300'
            }`} />
            <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{agent}</span>
                    <span className="text-xs text-gray-500">{time}</span>
                </div>
                <div className="text-sm text-gray-600">{message}</div>
            </div>
        </div>
    );
}

export default ProjectOverview;
